/**
 * tradeoff.ts — combine verdict + school density + safety into a single
 * tradeoff signal. Pure derivation. No score change, no new data, no
 * dependence on user lens or priority.
 *
 * Why this exists:
 *   The buyer wants to answer "should I trust this place?" in <30
 *   seconds. After fixing the safety signal we have three honest
 *   inputs:
 *
 *     verdict   green / amber / red             — overall
 *     density   high / medium / low             — schooling presence
 *     safety    safe / moderate / risky         — incident activity
 *
 *   The interesting cases are the *contradictions* — where the headline
 *   verdict is rosier or harsher than one of the underlying signals.
 *   Surfacing those contradictions explicitly is the cheapest decision
 *   aid we can ship without inventing a scoring system.
 *
 * Cases (per the brief):
 *   A. Good verdict + LOW schools  → "Good overall, but weak school access"
 *   B. Good verdict + RISKY safety → "Good overall, but safety concerns nearby"
 *   C. HIGH schools + RISKY safety → "Strong schooling, but safety concerns"
 *
 * Resolution rules — read before changing:
 *   1. "Good verdict" = green. Amber already telegraphs trade-offs in
 *      the verdict colour itself; flagging a tradeoff on amber would be
 *      double-counting. Red verdicts dominate the read — there is no
 *      "good" lens on red worth contradicting.
 *   2. Cases stack in priority B > A > C. Safety beats school gap
 *      because safety is the more decision-blocking concern. Case C is
 *      lowest because it needs both halves of its premise (HIGH + RISKY)
 *      to fire and is rare.
 *   3. If safety isn't ready yet (`ready: false`), tradeoff is `null`
 *      rather than guessed. We do NOT fall through to "case A" silently
 *      — that would mask the missing-data state from the user.
 *   4. Visual: ONE indicator at a time. The map's ⚠ glyph is a single
 *      symbol layer with a boolean filter — we never show two overlays
 *      on the same pin.
 */

import type { Verdict } from "./verdicts";
import { getSchoolDensity, type SchoolDensityLevel } from "./school-density";
import { getSafety, type SafetyLevel } from "./safety-signal";

export type TradeoffCase = "schoolGap" | "safetyGap" | "schoolVsSafety";

export interface Tradeoff {
  case: TradeoffCase;
  /** Headline label for the verdict-card row + map tooltip alt-text. */
  label: string;
  /** One-line reason copy. Same string used in card + future tooltip. */
  reason: string;
}

const CASE_COPY: Record<TradeoffCase, { label: string; reason: string }> = {
  schoolGap: {
    label: "Tradeoff detected",
    reason: "Good overall, but weak school access",
  },
  safetyGap: {
    label: "Tradeoff detected",
    reason: "Good overall, but safety concerns nearby",
  },
  schoolVsSafety: {
    label: "Tradeoff detected",
    reason: "Strong schooling, but safety concerns",
  },
};

export interface TradeoffInputs {
  verdict: Verdict;
  city: "hyd" | "mel" | "blr" | "syd";
}

/**
 * Returns the dominant tradeoff for a verdict, or `null` if none.
 *
 * Prefers safetyGap > schoolGap > schoolVsSafety so the most
 * decision-blocking contradiction wins when multiple apply.
 */
export function getTradeoff(opts: TradeoffInputs): Tradeoff | null {
  const { verdict, city } = opts;

  const density = getSchoolDensity(verdict.id);
  const safety = getSafety(verdict.id, city);

  // No safety opinion yet — refuse to guess. Surface as "no tradeoff"
  // rather than "case A" because case A's accuracy depends on a known
  // safety state too (a green-with-low-schools-AND-risky pin should
  // pick safetyGap, not schoolGap).
  if (!safety.ready) return null;

  const isGood = verdict.verdict === "green";

  // B beats A: safety is more decision-blocking than school gap.
  if (isGood && safety.level === "risky") {
    return { case: "safetyGap", ...CASE_COPY.safetyGap };
  }
  if (isGood && density.level === "low") {
    return { case: "schoolGap", ...CASE_COPY.schoolGap };
  }
  // C is independent of verdict colour — explicit in the brief.
  if (density.level === "high" && safety.level === "risky") {
    return { case: "schoolVsSafety", ...CASE_COPY.schoolVsSafety };
  }
  return null;
}

/* -------------------------------------------------------------------------- */
/*  Convenience for map paint-expression stamping.                            */
/*                                                                            */
/*  buildFeatures() in main-simple.ts stamps `hasTradeoff: boolean` and       */
/*  `tradeoffCase: TradeoffCase | "" ` onto each feature so the symbol layer  */
/*  can filter on `["get", "hasTradeoff"]` without re-running the logic.     */
/* -------------------------------------------------------------------------- */

export type TradeoffFeatureProps =
  | { hasTradeoff: false; tradeoffCase: "" }
  | { hasTradeoff: true; tradeoffCase: TradeoffCase };

export function tradeoffFeatureProps(
  opts: TradeoffInputs
): TradeoffFeatureProps {
  const t = getTradeoff(opts);
  if (!t) return { hasTradeoff: false, tradeoffCase: "" };
  return { hasTradeoff: true, tradeoffCase: t.case };
}

/* -------------------------------------------------------------------------- */
/*  Re-exports — keep the import surface small for callers.                   */
/* -------------------------------------------------------------------------- */

export type { SchoolDensityLevel, SafetyLevel };
